import { useEffect, useState } from "react";
import { PD } from "@/constants/testIds";
import {
  loadTableRows,
  sanitizeTableContent,
  selectedTableColumns,
  tableCellValue,
} from "@/lib/pageDesignerTable";

export default function PageDesignerTableContent({ card, preview = false }) {
  const config = sanitizeTableContent(card?.tableContent);
  const columns = selectedTableColumns(config);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    loadTableRows(config).then((result) => {
      if (cancelled) return;
      setRows(result.rows || []);
      setStatus(result.status);
    });
    return () => {
      cancelled = true;
    };
    // Intentional: reload when the saved table configuration changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config.sourceId,
    config.previewPatientId,
    config.sortColumn,
    config.sortDirection,
    config.maxRows,
    config.columns.join(","),
  ]);

  const title = config.title || "Table";
  let body = null;
  if (!config.sourceId) {
    body = <p className="epd__table-empty">Configure this table.</p>;
  } else if (!columns.length) {
    body = <p className="epd__table-empty">Select columns for this table.</p>;
  } else if (status === "need-patient") {
    body = <p className="epd__table-empty">Choose a preview patient to load rows.</p>;
  } else if (status === "loading" || status === "idle") {
    body = <p className="epd__table-empty">Loading table…</p>;
  } else if (status === "error") {
    body = <p className="epd__table-empty">Unable to load this table.</p>;
  } else if (!rows.length) {
    body = (
      <div className="epd__table-wrap">
        <table className="epd__data-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.id}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={columns.length}>No rows</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    body = (
      <div className="epd__table-wrap">
        <table className="epd__data-table" data-testid={`${PD.tableGrid}-${card.id}`}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.id}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.id || `${card.id}-row-${index}`}>
                {columns.map((column) => (
                  <td key={column.id}>{tableCellValue(config.sourceId, row, column.id) || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      className="epd__table"
      data-testid={`${PD.tableContent}-${card.id}`}
      data-preview={preview ? "true" : "false"}
    >
      <div className="epd__table-title" data-testid={`${PD.tableHeading}-${card.id}`}>
        {title}
      </div>
      {body}
    </div>
  );
}
